import React, { useRef } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { CustomElementConfig, Theme } from '../types';
import { RotateIcon } from './icons';

interface CustomElementItemProps {
  element: CustomElementConfig;
  pixelScale: number;
  onDrag: (id: string, x: number, y: number) => void;
  onRotate: (id: string) => void;
  isSelected: boolean;
  onSelect: (id: string | null) => void;
  venueWidthPx: number;
  venueHeightPx: number;
  theme: 'light' | 'dark';
}

const CustomElementItem: React.FC<CustomElementItemProps> = ({
  element, pixelScale, onDrag, onRotate, isSelected, onSelect, venueWidthPx, venueHeightPx, theme
}) => {
  const draggableRef = useRef<HTMLDivElement>(null);
  
  const contentWidthPx = element.width * pixelScale;
  const contentHeightPx = element.height * pixelScale;

  const draggableContainerWidthPx = element.orientation === 'vertical' ? contentHeightPx : contentWidthPx;
  const draggableContainerHeightPx = element.orientation === 'vertical' ? contentWidthPx : contentHeightPx;

  const position = {
    x: element.x * pixelScale - draggableContainerWidthPx / 2,
    y: element.y * pixelScale - draggableContainerHeightPx / 2,
  };
  
  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    const newCenterX = (data.x + draggableContainerWidthPx / 2) / pixelScale;
    const newCenterY = (data.y + draggableContainerHeightPx / 2) / pixelScale;
    onDrag(element.id, newCenterX, newCenterY);
  };

  const handleRotateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRotate(element.id);
  };

  const bgColor = theme === 'dark' ? 'bg-slate-600/70' : 'bg-slate-400/70'; // Distinct color
  const textColor = theme === 'dark' ? 'text-slate-100' : 'text-slate-800';
  const borderColor = theme === 'dark' ? 'border-slate-400' : 'border-slate-500';
  const selectionOutline = isSelected ? (theme === 'dark' ? 'outline outline-2 outline-offset-2 outline-sky-400' : 'outline outline-2 outline-offset-2 outline-sky-500') : '';


  return (
    <Draggable
      position={position}
      onStop={handleDrag} 
      bounds={{ 
        left: 0, 
        top: 0, 
        right: venueWidthPx - draggableContainerWidthPx, 
        bottom: venueHeightPx - draggableContainerHeightPx 
      }}
      nodeRef={draggableRef}
      cancel=".no-drag-ce-action" 
    >
      <div
        ref={draggableRef} 
        style={{
          width: `${draggableContainerWidthPx}px`,
          height: `${draggableContainerHeightPx}px`,
          position: 'absolute',
          touchAction: 'none', 
        }}
        className={`cursor-grab group ${selectionOutline} z-15`} // z-index between tables and dance floor potentially
        onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
      >
        <div
          style={{
            width: `${contentWidthPx}px`, 
            height: `${contentHeightPx}px`,
            transform: `translate(-50%, -50%) ${element.orientation === 'vertical' ? 'rotate(90deg)' : 'rotate(0deg)'}`,
            transformOrigin: 'center center',
            position: 'absolute', 
            top: '50%', 
            left: '50%',
            borderRadius: '0.125rem', // Slight rounding for a more "block" feel
          }}
          className={`flex flex-col items-center justify-center p-1 ${bgColor} border ${borderColor} ${textColor} text-xs sm:text-sm font-medium shadow-md`}
        >
          <span className="truncate max-w-full px-1">{element.name}</span>
          <span className="text-xs opacity-80">
            {element.width.toFixed(1)}m x {element.height.toFixed(1)}m
          </span>
        </div>

        {isSelected && (
          <button
            onClick={handleRotateClick}
            className={`no-drag-ce-action absolute -bottom-2.5 -right-2.5 p-1.5 rounded-full ${theme === 'dark' ? 'bg-hba-mid-purple text-hba-accent-pink' : 'bg-white text-purple-600'} shadow-lg hover:bg-opacity-80 z-30`}
            aria-label={`Rotar ${element.name}`}
          >
            <RotateIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
      </div>
    </Draggable>
  );
};

export default CustomElementItem;